import { useMemo } from 'react'

import { Text } from '@wartek-id/text-v1'

import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import { PerencanaanReviewDiff } from 'app/KinerjaShared/components/PerencanaanReviewDiff'

import type {
  PerencanaanItemReviewDiff,
  PerilakuKerjaItem,
} from 'app/KinerjaGuruPage/types/perencanaan'
import type { PerilakuKerjaAspek } from 'types/kinerja'

export type PerilakuKerjaItemProps = {
  originalItems: Array<PerilakuKerjaItem>
  reviewItems: Array<PerilakuKerjaItem>
  showReview: boolean
  contents: Array<PerilakuKerjaAspek>

  originalLabel?: string
  reviewLabel?: string
}

type ContentItem = {
  id: number
  label: string
}

export function PerilakuKerjaItems({
  originalItems,
  reviewItems,
  showReview,
  contents,
  originalLabel = 'Perencanaan Guru',
  reviewLabel = 'Penyesuaian dari Kepala Sekolah',
}: PerilakuKerjaItemProps) {
  // Content as map for constant time lookup
  const contentsMap: {
    aspekContents: Record<number, ContentItem>
    indikatorContents: Record<number, ContentItem>
    ekspektasiContents: Record<number, ContentItem>
  } = useMemo(() => {
    const aspekContents: Record<number, ContentItem> = {}
    const indikatorContents: Array<ContentItem> = []
    const ekspektasiContents: Array<ContentItem> = []

    contents.forEach((content) => {
      aspekContents[content.id] = { id: content.id, label: content.value }

      content.indikator.forEach((indikator) => {
        indikatorContents[indikator.id] = {
          id: indikator.id,
          label: indikator.value,
        }

        indikator.ekspektasi.forEach((ekspektasi) => {
          ekspektasiContents[ekspektasi.id] = {
            id: ekspektasi.id,
            label: ekspektasi.value,
          }
        })
      })
    })

    return {
      aspekContents,
      indikatorContents,
      ekspektasiContents,
    }
  }, [contents])

  // Resolved items
  const resolvedItems: Array<PerencanaanItemReviewDiff<PerilakuKerjaItem>> =
    useMemo(() => {
      let items: Array<PerencanaanItemReviewDiff<PerilakuKerjaItem>> = []

      if (showReview) {
        items = reviewItems.map((reviewItem) => ({
          isChanged: reviewItem.isChanged,
          original: originalItems.find(
            (item) => item.aspekId === reviewItem.aspekId
          ),
          review: reviewItem,
        }))
      } else {
        items = originalItems.map((originalItem) => ({
          isChanged: false,
          original: originalItem,
          review: null,
        }))
      }

      return items
    }, [originalItems, reviewItems, showReview])

  return (
    <TablePelaksanaanKinerja>
      <TableHeader>
        <TableHeaderItem className="w-12">No.</TableHeaderItem>
        <TableHeaderItem>Aspek</TableHeaderItem>
        <TableHeaderItem>Indikator Perilaku</TableHeaderItem>
        <TableHeaderItem>Ekspektasi Khusus Pimpinan</TableHeaderItem>
        <TableHeaderItem></TableHeaderItem>
      </TableHeader>
      <TableBody>
        {resolvedItems.map((resolvedItem, index) => {
          const originalAspekLabel =
            contentsMap.aspekContents[resolvedItem.original?.aspekId]?.label
          const originalIndikatorLabel =
            contentsMap.indikatorContents[resolvedItem.original?.indikatorId]
              ?.label
          const originalEkspektasiLabel =
            contentsMap.ekspektasiContents[resolvedItem.original?.ekspektasiId]
              ?.label

          const reviewAspekLabel =
            contentsMap.aspekContents[resolvedItem.review?.aspekId]?.label
          const reviewIndikatorLabel =
            contentsMap.indikatorContents[resolvedItem.review?.indikatorId]
              ?.label
          const reviewEkspektasiLabel =
            contentsMap.ekspektasiContents[resolvedItem.review?.ekspektasiId]
              ?.label

          return (
            <TableRow key={`perilaku-kerja-${index}`}>
              <TableRowItem>{index + 1}</TableRowItem>
              <TableRowItem>
                {showReview ? reviewAspekLabel : originalAspekLabel}
              </TableRowItem>
              <TableRowItem>
                {showReview ? reviewIndikatorLabel : originalIndikatorLabel}
              </TableRowItem>
              <TableRowItem>
                {showReview ? reviewEkspektasiLabel : originalEkspektasiLabel}
              </TableRowItem>
              <TableRowItem>
                {showReview && resolvedItem.isChanged ? (
                  <PerencanaanReviewDiff
                    originalLabel={originalLabel}
                    reviewLabel={reviewLabel}
                    originalContent={
                      <PerilakuKerjaDiffItem
                        aspek={originalAspekLabel}
                        indikator={originalIndikatorLabel}
                        ekspektasi={originalEkspektasiLabel}
                      />
                    }
                    reviewContent={
                      <PerilakuKerjaDiffItem
                        aspek={reviewAspekLabel}
                        indikator={reviewIndikatorLabel}
                        ekspektasi={reviewEkspektasiLabel}
                      />
                    }
                  />
                ) : null}
              </TableRowItem>
            </TableRow>
          )
        })}
      </TableBody>
    </TablePelaksanaanKinerja>
  )
}

type PerilakuKerjaDiffItemProps = {
  aspek: string
  indikator: string
  ekspektasi: string
}
function PerilakuKerjaDiffItem({
  aspek,
  indikator,
  ekspektasi,
}: PerilakuKerjaDiffItemProps) {
  return (
    <div className="space-y-5 border border-subdued rounded p-6">
      <Text variant="heading-md">{aspek}</Text>
      <div className="space-y-2">
        <Text color="subdued">Indikator Perilaku</Text>
        <Text variant="body-bold">{indikator}</Text>
      </div>
      <div className="space-y-2">
        <Text color="subdued">Ekspektasi Khusus Pimpinan</Text>
        <Text variant="body-bold">{ekspektasi}</Text>
      </div>
    </div>
  )
}
